export interface BlogPost {
  slug: string;
  caniuseFeature: string;
  caniuseEmbedFeature?: string;
  caniuseFeature2?: string;
  hideCaniuseSupport?: boolean;
  title: string;
  description: string;
  date: string;
  author: string;
  authorLink: string;
  tags: string[];
  readTime: string;
  cursorText: string;
}

const postImageUrls = import.meta.glob("/src/routes/posts/*/*.{png,jpg,jpeg}", {
  eager: true,
  import: "default",
  query: "?url",
}) as Record<string, string>;

// Resolve blog post image to a built static asset URL
export function getBlogImage(slug: string): string {
  // Try to find any image file in the post directory
  const matchingImage = Object.keys(postImageUrls).find((path) =>
    path.startsWith(`/src/routes/posts/${slug}/`),
  );

  return matchingImage ? postImageUrls[matchingImage] : `/og/${slug}.jpg`;
}

// Resolve blog post image to an absolute URL for Open Graph tags
export function getBlogImageAbsolute(slug: string): string {
  return `https://dinglandia.design/og/${slug}.jpg`;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "hydration",
    caniuseFeature: "es6-module",
    title: "Hydration",
    description:
      "A practical guide to Hydration with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "6 min read",
    cursorText: "⚛️ Learn Hydration",
  },
  {
    slug: "partial-hydration",
    caniuseFeature: "es6-module",
    title: "Partial hydration",
    description:
      "A practical guide to Partial hydration with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Partial hydration",
  },
  {
    slug: "islands-architecture",
    caniuseFeature: "es6-module",
    title: "Islands architecture",
    description:
      "A practical guide to Islands architecture with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Islands architecture",
  },
  {
    slug: "streaming-ssr",
    caniuseFeature: "es6-module",
    title: "Streaming SSR",
    description:
      "A practical guide to Streaming SSR with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Streaming SSR",
  },
  {
    slug: "concurrent-rendering",
    caniuseFeature: "es6-module",
    title: "Concurrent rendering",
    description:
      "A practical guide to Concurrent rendering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Concurrent rendering",
  },
  {
    slug: "time-slicing",
    caniuseFeature: "es6-module",
    title: "Time slicing",
    description:
      "A practical guide to Time slicing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Time slicing",
  },
  {
    slug: "reconciliation-algorithm",
    caniuseFeature: "es6-module",
    title: "Reconciliation algorithm",
    description:
      "A practical guide to Reconciliation algorithm with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Reconciliation algorithm",
  },
  {
    slug: "fiber-architecture",
    caniuseFeature: "es6-module",
    title: "Fiber architecture",
    description:
      "A practical guide to Fiber architecture with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Fiber architecture",
  },
  {
    slug: "virtual-dom-diffing-complexity",
    caniuseFeature: "es6-module",
    title: "Virtual DOM diffing complexity",
    description:
      "A practical guide to Virtual DOM diffing complexity with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "9 min read",
    cursorText: "⚛️ Learn Virtual DOM diffing complexity",
  },
  {
    slug: "structural-sharing",
    caniuseFeature: "es6-module",
    title: "Structural sharing",
    description:
      "A practical guide to Structural sharing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🧩 Learn Structural sharing",
  },
  {
    slug: "immutable-data-patterns",
    caniuseFeature: "es6-module",
    title: "Immutable data patterns",
    description:
      "A practical guide to Immutable data patterns with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧩 Learn Immutable data patterns",
  },
  {
    slug: "referential-equality",
    caniuseFeature: "es6-module",
    title: "Referential equality",
    description:
      "A practical guide to Referential equality with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🧩 Learn Referential equality",
  },
  {
    slug: "memoization-pitfalls",
    caniuseFeature: "es6-module",
    title: "Memoization pitfalls",
    description:
      "A practical guide to Memoization pitfalls with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🧩 Learn Memoization pitfalls",
  },
  {
    slug: "stale-closure-problem",
    caniuseFeature: "es6-module",
    title: "Stale closure problem",
    description:
      "A practical guide to Stale closure problem with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧩 Learn Stale closure problem",
  },
  {
    slug: "event-loop-macro-vs-microtasks",
    caniuseFeature: "es6-module",
    title: "Event loop (macro vs microtasks)",
    description:
      "A practical guide to Event loop (macro vs microtasks) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Web Vitals", "Performance", "Metrics"],
    readTime: "10 min read",
    cursorText: "🧠 Learn Event loop (macro vs microtasks)",
  },
  {
    slug: "task-starvation",
    caniuseFeature: "es6-module",
    title: "Task starvation",
    description:
      "A practical guide to Task starvation with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🚦 Learn Task starvation",
  },
  {
    slug: "layout-thrashing",
    caniuseFeature: "es6-module",
    title: "Layout thrashing",
    description:
      "A practical guide to Layout thrashing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "7 min read",
    cursorText: "🎨 Learn Layout thrashing",
  },
  {
    slug: "critical-rendering-path",
    caniuseFeature: "es6-module",
    title: "Critical rendering path",
    description:
      "A practical guide to Critical rendering path with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "8 min read",
    cursorText: "🚦 Learn Critical rendering path",
  },
  {
    slug: "render-blocking-resources",
    caniuseFeature: "es6-module",
    title: "Render blocking resources",
    description:
      "A practical guide to Render blocking resources with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "8 min read",
    cursorText: "🚦 Learn Render blocking resources",
  },
  {
    slug: "tree-shaking-internals",
    caniuseFeature: "es6-module",
    title: "Tree shaking internals",
    description:
      "A practical guide to Tree shaking internals with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "📦 Learn Tree shaking internals",
  },
  {
    slug: "code-splitting-strategies",
    caniuseFeature: "es6-module",
    title: "Code splitting strategies",
    description:
      "A practical guide to Code splitting strategies with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "📦 Learn Code splitting strategies",
  },
  {
    slug: "dynamic-import-chunking",
    caniuseFeature: "es6-module",
    title: "Dynamic import chunking",
    description:
      "A practical guide to Dynamic import chunking with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "📦 Learn Dynamic import chunking",
  },
  {
    slug: "module-federation",
    caniuseFeature: "es6-module",
    title: "Module federation",
    description:
      "A practical guide to Module federation with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "📦 Learn Module federation",
  },
  {
    slug: "shadow-dom",
    caniuseFeature: "shadowdomv1",
    title: "Shadow DOM",
    description:
      "A practical guide to Shadow DOM with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🧱 Learn Shadow DOM",
  },
  {
    slug: "custom-elements-lifecycle",
    caniuseFeature: "custom-elementsv1",
    title: "Custom Elements lifecycle",
    description:
      "A practical guide to Custom Elements lifecycle with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧱 Learn Custom Elements lifecycle",
  },
  {
    slug: "web-components-interoperability",
    caniuseFeature: "wf-scoped-custom-element-registries",
    title: "Scoped Custom Element Registries",
    description:
      "Isolate custom element definitions per shadow root so independently versioned web components can coexist without global tag name collisions.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Web Components", "Custom Elements", "Architecture"],
    readTime: "7 min read",
    cursorText: "🧱 Scope Component Registries",
  },
  {
    slug: "web-workers-vs-service-workers",
    caniuseFeature: "serviceworkers",
    title: "Web Workers vs Service Workers",
    description:
      "A practical guide to Web Workers vs Service Workers with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "10 min read",
    cursorText: "🧰 Learn Web Workers vs Service Workers",
  },
  {
    slug: "sharedarraybuffer",
    caniuseFeature: "sharedarraybuffer",
    title: "SharedArrayBuffer",
    description:
      "A practical guide to SharedArrayBuffer with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "6 min read",
    cursorText: "🧪 Learn SharedArrayBuffer",
  },
  {
    slug: "transferable-objects",
    caniuseFeature: "es6-module",
    title: "Transferable objects",
    description:
      "A practical guide to Transferable objects with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🧪 Learn Transferable objects",
  },
  {
    slug: "offscreencanvas",
    caniuseFeature: "offscreencanvas",
    title: "OffscreenCanvas",
    description:
      "A practical guide to OffscreenCanvas with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "6 min read",
    cursorText: "🧪 Learn OffscreenCanvas",
  },
  {
    slug: "webassembly-integration",
    caniuseFeature: "mdn-webassembly_jspi",
    title: "JSPI for WebAssembly",
    description:
      "Bridge async JavaScript and synchronous-looking WebAssembly exports without hand-rolled state machines by using the JavaScript Promise Integration.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "WebAssembly", "JavaScript", "Performance"],
    readTime: "7 min read",
    cursorText: "🧪 Async Wasm Bridges",
  },
  {
    slug: "browser-compositing-layers",
    caniuseFeature: "es6-module",
    title: "Browser compositing layers",
    description:
      "A practical guide to Browser compositing layers with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🎨 Learn Browser compositing layers",
  },
  {
    slug: "paint-vs-composite-vs-layout",
    caniuseFeature: "es6-module",
    title: "Paint vs composite vs layout",
    description:
      "A practical guide to Paint vs composite vs layout with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "10 min read",
    cursorText: "🎨 Learn Paint vs composite vs layout",
  },
  {
    slug: "gpu-acceleration-in-css",
    caniuseFeature: "es6-module",
    title: "GPU acceleration in CSS",
    description:
      "A practical guide to GPU acceleration in CSS with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "9 min read",
    cursorText: "🎨 Learn GPU acceleration in CSS",
  },
  {
    slug: "css-containment",
    caniuseFeature: "es6-module",
    title: "CSS containment",
    description:
      "A practical guide to CSS containment with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "7 min read",
    cursorText: "🎨 Learn CSS containment",
  },
  {
    slug: "subpixel-rendering",
    caniuseFeature: "es6-module",
    title: "Subpixel rendering",
    description:
      "A practical guide to Subpixel rendering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "🎨 Learn Subpixel rendering",
  },
  {
    slug: "intersectionobserver-internals",
    caniuseFeature: "intersectionobserver",
    title: "IntersectionObserver internals",
    description:
      "A practical guide to IntersectionObserver internals with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "7 min read",
    cursorText: "🧪 Learn IntersectionObserver internals",
  },
  {
    slug: "resizeobserver-loop-limits",
    caniuseFeature: "resizeobserver",
    title: "ResizeObserver loop limits",
    description:
      "A practical guide to ResizeObserver loop limits with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "8 min read",
    cursorText: "🧪 Learn ResizeObserver loop limits",
  },
  {
    slug: "mutationobserver-cost",
    caniuseFeature: "mutationobserver",
    title: "MutationObserver cost",
    description:
      "A practical guide to MutationObserver cost with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "7 min read",
    cursorText: "🧪 Learn MutationObserver cost",
  },
  {
    slug: "indexeddb",
    caniuseFeature: "wf-getallrecords",
    title: "IndexedDB getAllRecords()",
    description:
      "Use getAllRecords() to read keys and values together so cursor-heavy IndexedDB code stays smaller, faster, and easier to reason about.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "IndexedDB", "Offline", "Browser APIs"],
    readTime: "6 min read",
    cursorText: "🗂️ Read IDB Records",
  },
  {
    slug: "service-worker-lifecycle-traps",
    caniuseFeature: "serviceworkers",
    title: "Service Worker lifecycle traps",
    description:
      "A practical guide to Service Worker lifecycle traps with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "9 min read",
    cursorText: "🧰 Learn Service Worker lifecycle traps",
  },
  {
    slug: "cache-invalidation-strategies",
    caniuseFeature: "es6-module",
    title: "Cache invalidation strategies",
    description:
      "A practical guide to Cache invalidation strategies with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Networking", "Performance", "Caching"],
    readTime: "8 min read",
    cursorText: "🌐 Learn Cache invalidation strategies",
  },
  {
    slug: "stale-while-revalidate",
    caniuseFeature: "es6-module",
    title: "Stale-while-revalidate",
    description:
      "A practical guide to Stale-while-revalidate with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "6 min read",
    cursorText: "🌐 Learn Stale-while-revalidate",
  },
  {
    slug: "etag-vs-cache-control",
    caniuseFeature: "es6-module",
    title: "ETag vs Cache-Control",
    description:
      "A practical guide to ETag vs Cache-Control with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Networking", "Performance", "Caching"],
    readTime: "8 min read",
    cursorText: "🌐 Learn ETag vs Cache-Control",
  },
  {
    slug: "http-3-and-quic",
    caniuseFeature: "es6-module",
    title: "HTTP/3 and QUIC",
    description:
      "A practical guide to HTTP/3 and QUIC with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Networking", "Performance", "Caching"],
    readTime: "8 min read",
    cursorText: "🌐 Learn HTTP/3 and QUIC",
  },
  {
    slug: "priority-hints",
    caniuseFeature: "es6-module",
    title: "Priority hints",
    description:
      "A practical guide to Priority hints with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "🌐 Learn Priority hints",
  },
  {
    slug: "preload-vs-prefetch-vs-preconnect",
    caniuseFeature: "es6-module",
    title: "Preload vs Prefetch vs Preconnect",
    description:
      "A practical guide to Preload vs Prefetch vs Preconnect with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Networking", "Performance", "Caching"],
    readTime: "10 min read",
    cursorText: "🌐 Learn Preload vs Prefetch vs Preconnect",
  },
  {
    slug: "cors-preflight",
    caniuseFeature: "es6-module",
    title: "CORS preflight",
    description:
      "A practical guide to CORS preflight with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "7 min read",
    cursorText: "🔒 Learn CORS preflight",
  },
  {
    slug: "samesite-cookie-modes",
    caniuseFeature: "same-site-cookie-attribute",
    title: "SameSite cookie modes",
    description:
      "A practical guide to SameSite cookie modes with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "8 min read",
    cursorText: "🔒 Learn SameSite cookie modes",
  },
  {
    slug: "csrf-vs-xss-mitigation",
    caniuseFeature: "es6-module",
    title: "CSRF vs XSS mitigation",
    description:
      "A practical guide to CSRF vs XSS mitigation with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "9 min read",
    cursorText: "🔒 Learn CSRF vs XSS mitigation",
  },
  {
    slug: "content-security-policy-csp",
    caniuseFeature: "contentsecuritypolicy",
    title: "Content Security Policy (CSP)",
    description:
      "A practical guide to Content Security Policy (CSP) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "9 min read",
    cursorText: "🔒 Learn Content Security Policy (CSP)",
  },
  {
    slug: "trusted-types",
    caniuseFeature: "es6-module",
    title: "Trusted Types",
    description:
      "A practical guide to Trusted Types with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "7 min read",
    cursorText: "🔒 Learn Trusted Types",
  },
  {
    slug: "dom-clobbering",
    caniuseFeature: "es6-module",
    title: "DOM clobbering",
    description:
      "A practical guide to DOM clobbering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "7 min read",
    cursorText: "🔒 Learn DOM clobbering",
  },
  {
    slug: "prototype-pollution",
    caniuseFeature: "es6-module",
    title: "Prototype pollution",
    description:
      "A practical guide to Prototype pollution with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web Platform", "Frontend"],
    readTime: "7 min read",
    cursorText: "🔒 Learn Prototype pollution",
  },
  {
    slug: "race-conditions-in-ui-state",
    caniuseFeature: "es6-module",
    title: "Race conditions in UI state",
    description:
      "A practical guide to Race conditions in UI state with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "10 min read",
    cursorText: "🧩 Learn Race conditions in UI state",
  },
  {
    slug: "tearing-in-concurrent-ui",
    caniuseFeature: "es6-module",
    title: "Tearing in concurrent UI",
    description:
      "A practical guide to Tearing in concurrent UI with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "9 min read",
    cursorText: "⚛️ Learn Tearing in concurrent UI",
  },
  {
    slug: "scheduler-priorities",
    caniuseFeature: "es6-module",
    title: "Scheduler priorities",
    description:
      "A practical guide to Scheduler priorities with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Scheduler priorities",
  },
  {
    slug: "render-waterfalls",
    caniuseFeature: "es6-module",
    title: "Render waterfalls",
    description:
      "A practical guide to Render waterfalls with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "🚦 Learn Render waterfalls",
  },
  {
    slug: "suspense-boundaries",
    caniuseFeature: "es6-module",
    title: "Suspense boundaries",
    description:
      "A practical guide to Suspense boundaries with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Suspense boundaries",
  },
  {
    slug: "selective-hydration",
    caniuseFeature: "es6-module",
    title: "Selective hydration",
    description:
      "A practical guide to Selective hydration with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Selective hydration",
  },
  {
    slug: "server-components",
    caniuseFeature: "es6-module",
    title: "Server components",
    description:
      "A practical guide to Server components with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Server components",
  },
  {
    slug: "edge-rendering",
    caniuseFeature: "es6-module",
    title: "Edge rendering",
    description:
      "A practical guide to Edge rendering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Edge rendering",
  },
  {
    slug: "micro-frontend-orchestration",
    caniuseFeature: "es6-module",
    title: "Micro-frontend orchestration",
    description:
      "A practical guide to Micro-frontend orchestration with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "📦 Learn Micro-frontend orchestration",
  },
  {
    slug: "finite-state-modeling",
    caniuseFeature: "es6-module",
    title: "Finite state modeling",
    description:
      "A practical guide to Finite state modeling with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧩 Learn Finite state modeling",
  },
  {
    slug: "event-sourcing-in-frontend",
    caniuseFeature: "es6-module",
    title: "Event sourcing in frontend",
    description:
      "A practical guide to Event sourcing in frontend with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "9 min read",
    cursorText: "🧩 Learn Event sourcing in frontend",
  },
  {
    slug: "optimistic-ui-rollback-strategy",
    caniuseFeature: "es6-module",
    title: "Optimistic UI rollback strategy",
    description:
      "A practical guide to Optimistic UI rollback strategy with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "9 min read",
    cursorText: "🧩 Learn Optimistic UI rollback strategy",
  },
  {
    slug: "offline-conflict-resolution",
    caniuseFeature: "es6-module",
    title: "Offline conflict resolution",
    description:
      "A practical guide to Offline conflict resolution with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧩 Learn Offline conflict resolution",
  },
  {
    slug: "crdt-basics-for-collaboration",
    caniuseFeature: "es6-module",
    title: "CRDT basics for collaboration",
    description:
      "A practical guide to CRDT basics for collaboration with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "9 min read",
    cursorText: "🧩 Learn CRDT basics for collaboration",
  },
  {
    slug: "webrtc",
    caniuseFeature: "rtcpeerconnection",
    title: "WebRTC Improvements",
    description:
      "Ship more predictable audio, video, and data channel flows by treating negotiation, track state, and stats as first-class parts of your WebRTC architecture.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "WebRTC", "Realtime", "Media"],
    readTime: "8 min read",
    cursorText: "📡 Tune WebRTC",
  },
  {
    slug: "backpressure-in-streams-api",
    caniuseFeature: "es6-module",
    title: "Backpressure in streams API",
    description:
      "A practical guide to Backpressure in streams API with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "9 min read",
    cursorText: "🧪 Learn Backpressure in streams API",
  },
  {
    slug: "abortcontroller",
    caniuseFeature: "es6-module",
    title: "AbortController",
    description:
      "A practical guide to AbortController with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "6 min read",
    cursorText: "🧪 Learn AbortController",
  },
  {
    slug: "streaming-fetch-response-handling",
    caniuseFeature: "mdn-api_request_request_request_body_readablestream",
    caniuseFeature2: "streams",
    title: "Fetch Uploads and Ranges",
    description:
      "Stream request bodies, keep FormData and MIME handling explicit, and use Range requests for partial transfers without abandoning Fetch.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Fetch", "Streams", "Networking"],
    readTime: "8 min read",
    cursorText: "📤 Stream Fetch Uploads",
  },
  {
    slug: "browser-memory-leak-detection",
    caniuseFeature: "es6-module",
    title: "Browser memory leak detection",
    description:
      "A practical guide to Browser memory leak detection with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "9 min read",
    cursorText: "🧹 Learn Browser memory leak detection",
  },
  {
    slug: "detached-dom-nodes",
    caniuseFeature: "es6-module",
    title: "Detached DOM nodes",
    description:
      "A practical guide to Detached DOM nodes with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧹 Learn Detached DOM nodes",
  },
  {
    slug: "garbage-collection-timing",
    caniuseFeature: "es6-module",
    title: "Garbage collection timing",
    description:
      "A practical guide to Garbage collection timing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧹 Learn Garbage collection timing",
  },
  {
    slug: "performanceobserver-api",
    caniuseFeature: "es6-module",
    title: "PerformanceObserver API",
    description:
      "A practical guide to PerformanceObserver API with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Browser APIs", "JavaScript", "Performance"],
    readTime: "7 min read",
    cursorText: "📈 Learn PerformanceObserver API",
  },
  {
    slug: "long-tasks-api",
    caniuseFeature: "es6-module",
    title: "Long tasks API",
    description:
      "A practical guide to Long tasks API with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Web Vitals", "Performance", "Metrics"],
    readTime: "8 min read",
    cursorText: "📈 Learn Long tasks API",
  },
  {
    slug: "first-input-delay-fid",
    caniuseFeature: "es6-module",
    title: "First Input Delay (FID)",
    description:
      "A practical guide to First Input Delay (FID) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Web Vitals", "Performance", "Metrics"],
    readTime: "9 min read",
    cursorText: "📈 Learn First Input Delay (FID)",
  },
  {
    slug: "interaction-to-next-paint-inp",
    caniuseFeature: "es6-module",
    title: "Interaction to Next Paint (INP)",
    description:
      "A practical guide to Interaction to Next Paint (INP) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "10 min read",
    cursorText: "📈 Learn Interaction to Next Paint (INP)",
  },
  {
    slug: "cumulative-layout-shift-cls",
    caniuseFeature: "es6-module",
    title: "Cumulative Layout Shift (CLS)",
    description:
      "A practical guide to Cumulative Layout Shift (CLS) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "9 min read",
    cursorText: "📈 Learn Cumulative Layout Shift (CLS)",
  },
  {
    slug: "largest-contentful-paint-lcp",
    caniuseFeature: "es6-module",
    title: "Largest Contentful Paint (LCP)",
    description:
      "A practical guide to Largest Contentful Paint (LCP) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Rendering", "Performance"],
    readTime: "9 min read",
    cursorText: "📈 Learn Largest Contentful Paint (LCP)",
  },
  {
    slug: "speculative-prerendering",
    caniuseFeature: "es6-module",
    title: "Speculative prerendering",
    description:
      "A practical guide to Speculative prerendering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "🔮 Learn Speculative prerendering",
  },
  {
    slug: "priority-inversion-in-async-code",
    caniuseFeature: "es6-module",
    title: "Priority inversion in async code",
    description:
      "A practical guide to Priority inversion in async code with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "10 min read",
    cursorText: "🚦 Learn Priority inversion in async code",
  },
  {
    slug: "deterministic-rendering",
    caniuseFeature: "es6-module",
    title: "Deterministic rendering",
    description:
      "A practical guide to Deterministic rendering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Rendering", "Architecture", "Performance"],
    readTime: "7 min read",
    cursorText: "⚛️ Learn Deterministic rendering",
  },
  {
    slug: "idempotent-ui-actions",
    caniuseFeature: "es6-module",
    title: "Idempotent UI actions",
    description:
      "A practical guide to Idempotent UI actions with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "8 min read",
    cursorText: "🧩 Learn Idempotent UI actions",
  },
  {
    slug: "accessibility-tree",
    caniuseFeature: "es6-module",
    title: "Accessibility tree",
    description:
      "A practical guide to Accessibility tree with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Accessibility", "A11y", "Frontend"],
    readTime: "7 min read",
    cursorText: "♿ Learn Accessibility tree",
  },
  {
    slug: "aria-live-regions-internals",
    caniuseFeature: "es6-module",
    title: "ARIA live regions internals",
    description:
      "A practical guide to ARIA live regions internals with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Accessibility", "A11y", "Frontend"],
    readTime: "9 min read",
    cursorText: "♿ Learn ARIA live regions internals",
  },
  {
    slug: "pointer-events",
    caniuseFeature: "pointer",
    title: "Pointer events",
    description:
      "A practical guide to Pointer events with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Frontend Engineering", "Architecture", "JavaScript"],
    readTime: "7 min read",
    cursorText: "♿ Learn Pointer events",
  },
  {
    slug: "avif-image-format-guide",
    caniuseFeature: "avif",
    title: "AVIF and Modern Image Formats for the Web",
    description:
      "How AVIF can cut payload by over 90% in many pipelines, plus practical fallbacks for WebP, JPEG XL, SVG, Lottie, PNG, JPEG, GIF, and APNG.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["AVIF", "Performance", "Images", "WebP", "JPEG XL"],
    readTime: "11 min read",
    cursorText: "🖼️ Shrink Images Without Quality Loss",
  },
  {
    slug: "css-popover-api",
    caniuseFeature: "wf-popover",
    caniuseFeature2: "wf-dialog-closedby",
    title: "Dialogs and Popovers",
    description:
      "Ship modern overlays with popover hints, dialog closedby control, and the new open state selectors instead of rebuilding overlay behavior in JavaScript.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "HTML", "Dialog", "Popover"],
    readTime: "8 min read",
    cursorText: "🪟 Modern Overlays",
  },
  {
    slug: "css-style-queries",
    caniuseFeature: "css-container-queries-style",
    title: "Container Style Queries",
    description:
      "Query custom property values on a container so components can react to local tokens, density modes, and semantic variants without class soup.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Container Queries", "Design Systems"],
    readTime: "6 min read",
    cursorText: "🎛️ Query Container Styles",
  },
  {
    slug: "css-functions",
    caniuseFeature: "css-math-functions",
    title: "Modern CSS Functions",
    description:
      "Practical patterns for calc(), clamp(), min(), max(), color-mix(), light-dark(), and friends in production UI.",
    date: "2026-02-16",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Functions", "Layout"],
    readTime: "10 min read",
    cursorText: "🧮 Compose with CSS Functions",
  },
  {
    slug: "customizable-select",
    caniuseFeature: "selectlist",
    title: "Customizable Select",
    description:
      "Style native select controls with modern browser APIs while keeping accessibility and keyboard behavior intact.",
    date: "2026-02-16",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Forms", "CSS", "Accessibility"],
    readTime: "9 min read",
    cursorText: "🔽 Modernize Native Select",
  },
  {
    slug: "css-starting-style",
    caniuseFeature: "css-transitions",
    title: "CSS @starting-style",
    description:
      "Create entrance transitions for newly inserted UI with @starting-style and avoid first-frame animation jumps.",
    date: "2026-02-16",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Animation", "Transitions"],
    readTime: "7 min read",
    cursorText: "✨ Smooth First Frame Animations",
  },
  {
    slug: "invokers-api",
    caniuseFeature: "popover",
    caniuseEmbedFeature: "dialog",
    title: "Invokers API",
    description:
      "Trigger popovers and command-driven interactions declaratively with command and commandfor, with JS only where needed.",
    date: "2026-02-16",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["HTML", "Invokers", "Interaction"],
    readTime: "8 min read",
    cursorText: "🕹️ Wire Declarative Commands",
  },
  {
    slug: "css-scroll-timeline-animations",
    caniuseFeature: "web-animation",
    title: "Scroll Timeline Animations",
    description:
      "Drive animations with scroll progress using scroll and view timelines for smoother, more intentional reading flows.",
    date: "2026-02-16",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Animation", "Scroll"],
    readTime: "10 min read",
    cursorText: "📈 Animate with Scroll Progress",
  },
  {
    slug: "page-view-transitions",
    caniuseFeature: "view-transitions",
    caniuseFeature2: "cross-document-view-transitions",
    title: "View Transitions",
    description:
      "Coordinate same-document and cross-document transitions with types, render blocking, and route-aware animations instead of manually stitching snapshots together.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "View Transitions", "CSS", "Navigation"],
    readTime: "10 min read",
    cursorText: "🎬 Transition Between Views",
  },
  {
    slug: "css-color-contrast",
    caniuseFeature: "wf-contrast-color",
    title: "contrast-color() in CSS",
    description:
      "Let the browser pick a readable foreground for a background color, then layer audits and fallbacks where strict WCAG guarantees still matter.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Color", "Accessibility"],
    readTime: "6 min read",
    cursorText: "🎯 Auto-Pick Contrast",
  },
  {
    slug: "css-scroll-state-queries",
    caniuseFeature: "css-container-queries",
    title: "CSS Scroll State Queries",
    description:
      "React to sticky and snapped states in pure CSS with scroll-state container queries and clean progressive enhancement.",
    date: "2026-02-16",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Scrolling", "UX"],
    readTime: "8 min read",
    cursorText: "🧭 Query Scroll State in CSS",
  },
  {
    slug: "css-anchor-positioning",
    caniuseFeature: "css-anchor-positioning",
    title: "CSS Anchor Positioning",
    description:
      "Use anchors, fallbacks, and follow-the-leader placement to keep tooltips, menus, and teaching UI attached to the right element with less JS.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Layout", "UI"],
    readTime: "7 min read",
    cursorText: "📌 Follow the Leader",
  },
  {
    slug: "syntax-highlighting-font",
    caniuseFeature: "colr-v1",
    title: "Syntax Highlighting With Color Fonts",
    description:
      "Exploring the new color font technology for syntax highlighting with Monaspace - a revolutionary approach to code presentation.",
    date: "2026-02-15",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Typography", "Fonts", "Development"],
    readTime: "5 min read",
    cursorText: "📝 Explore the new color font technology",
  },
  {
    slug: "understanding-css-animations",
    caniuseFeature: "css-animation",
    title: "Understanding CSS Animations",
    description:
      "Deep dive into modern CSS animation techniques and best practices for creating smooth, performant animations.",
    date: "2026-02-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Animation", "Performance"],
    readTime: "8 min read",
    cursorText: "🎨 Dive into CSS Animations",
  },
  {
    slug: "web-performance-optimization",
    caniuseFeature: "loading-lazy-attr",
    title: "Performance Optimization Tips",
    description:
      "Practical tips for improving web performance and user experience, from Core Web Vitals to advanced optimization techniques.",
    date: "2026-02-05",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Performance", "Web Vitals", "Optimization"],
    readTime: "10 min read",
    cursorText: "⚡ Boost Your Performance",
  },
  {
    slug: "sveltekit-best-practices",
    caniuseFeature: "es6-module-dynamic-import",
    title: "SvelteKit Best Practices",
    description:
      "Learn how to build better applications with SvelteKit and modern tooling - from routing to server-side rendering.",
    date: "2026-01-28",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["SvelteKit", "JavaScript", "Best Practices"],
    readTime: "12 min read",
    cursorText: "🚀 Master SvelteKit",
  },
  {
    slug: "responsive-design-patterns",
    caniuseFeature: "css-mediaqueries",
    title: "Responsive Design Patterns",
    description:
      "Modern approaches to creating responsive layouts that work everywhere, from mobile to ultra-wide displays.",
    date: "2026-01-20",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Responsive Design", "CSS Grid", "Flexbox"],
    readTime: "7 min read",
    cursorText: "📱 Build Responsive Layouts",
  },
  {
    slug: "web-accessibility-guide",
    caniuseFeature: "wai-aria",
    title: "Web Accessibility Guide",
    description:
      "Making the web accessible to everyone with practical implementation tips for WCAG compliance and inclusive design.",
    date: "2026-01-15",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Accessibility", "A11y", "WCAG"],
    readTime: "9 min read",
    cursorText: "♿ Accessibility for All",
  },
  {
    slug: "modern-javascript-features",
    caniuseFeature: "es6-module",
    title: "Modern JavaScript Features",
    description:
      "Exploring the latest JavaScript features and how to use them effectively in your projects - ES2024 and beyond.",
    date: "2026-01-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["JavaScript", "ES2024", "Modern Web"],
    readTime: "11 min read",
    cursorText: "✨ Explore Modern JavaScript",
  },
  {
    slug: "design-systems-at-scale",
    caniuseFeature: "css-variables",
    title: "Design Systems at Scale",
    description:
      "Building and maintaining design systems for large applications - component libraries, tokens, and documentation.",
    date: "2026-01-01",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Design Systems", "UI", "Components"],
    readTime: "15 min read",
    cursorText: "🎯 Scale Your Design System",
  },
  {
    slug: "css-container-queries",
    caniuseFeature: "css-container-queries",
    title: "CSS Container Queries",
    description:
      "Transform your responsive design strategy with container queries - make components adapt to their container, not the viewport.",
    date: "2025-12-20",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Container Queries", "Responsive Design"],
    readTime: "12 min read",
    cursorText: "📦 Master Container Queries",
  },
  {
    slug: "css-has-selector",
    caniuseFeature: "css-has",
    title: "CSS :has() Selector - The Parent Selector",
    description:
      "The :has() pseudo-class brings parent selection to CSS, enabling powerful relational styling without JavaScript.",
    date: "2025-12-18",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Selectors", "Modern CSS"],
    readTime: "8 min read",
    cursorText: "🎯 Select Parents with :has()",
  },
  {
    slug: "css-subgrid",
    caniuseFeature: "css-subgrid",
    title: "CSS Subgrid - Inherit Grid Tracks",
    description:
      "CSS Subgrid allows nested grids to inherit track sizing from their parent, solving complex alignment challenges.",
    date: "2025-12-15",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Grid", "Layout"],
    readTime: "10 min read",
    cursorText: "📐 Align with Subgrid",
  },
  {
    slug: "css-nth-child-of",
    caniuseFeature: "css-nth-child-of",
    title: "CSS :nth-child(An+B of S) - Advanced Selection",
    description:
      "The enhanced :nth-child() selector with 'of S' syntax enables precise element selection based on complex criteria.",
    date: "2025-12-12",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Selectors", "Modern CSS"],
    readTime: "7 min read",
    cursorText: "🔢 Select with Precision",
  },
  {
    slug: "css-relative-colors",
    caniuseFeature: "css-relative-colors",
    title: "CSS Relative Colors - Dynamic Color Manipulation",
    description:
      "Create color variations dynamically with relative color syntax - adjust lightness, saturation, and alpha on the fly.",
    date: "2025-12-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Colors", "Design"],
    readTime: "9 min read",
    cursorText: "🎨 Transform Colors",
  },
  {
    slug: "css-scroll-snapping",
    caniuseFeature: "css-snappoints",
    title: "CSS Scroll Snap",
    description:
      "Build predictable carousels and paged surfaces with snap points, scroll padding, and keyboard-friendly fallbacks instead of brittle JS sliders.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Scroll", "UX"],
    readTime: "7 min read",
    cursorText: "🧲 Snap Intentionally",
  },
  {
    slug: "css-focus-visible",
    caniuseFeature: "css-focus-visible",
    title: "CSS :focus-visible - Better Focus Indicators",
    description:
      "Show focus indicators only when needed with :focus-visible - improve accessibility without compromising aesthetics.",
    date: "2025-12-05",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Accessibility", "UX"],
    readTime: "6 min read",
    cursorText: "👁️ Focus on Accessibility",
  },
  {
    slug: "css-focus-within",
    caniuseFeature: "css-focus-within",
    title: "CSS :focus-within - Style Parent Elements",
    description:
      "The :focus-within pseudo-class enables styling parent elements when any descendant receives focus.",
    date: "2025-12-03",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Selectors", "Forms"],
    readTime: "7 min read",
    cursorText: "🎯 Style with Context",
  },
  {
    slug: "css-user-valid-invalid",
    caniuseFeature: "form-validation",
    title: "CSS :user-valid and :user-invalid - Better Form Validation",
    description:
      "Improve form UX with :user-valid and :user-invalid - show validation only after user interaction.",
    date: "2025-12-01",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Forms", "Validation"],
    readTime: "8 min read",
    cursorText: "✅ Validate with Care",
  },
  {
    slug: "indexing",
    caniuseFeature: "es6-module",
    title: "Indexing",
    description:
      "A practical guide to Indexing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Indexing",
  },
  {
    slug: "clustering",
    caniuseFeature: "es6-module",
    title: "Clustering",
    description:
      "A practical guide to Clustering with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Clustering",
  },
  {
    slug: "denormalization",
    caniuseFeature: "es6-module",
    title: "Denormalization",
    description:
      "A practical guide to Denormalization with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Denormalization",
  },
  {
    slug: "normalization",
    caniuseFeature: "es6-module",
    title: "Normalization",
    description:
      "A practical guide to Normalization with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Normalization",
  },
  {
    slug: "read-replicas",
    caniuseFeature: "es6-module",
    title: "Read replicas",
    description:
      "A practical guide to Read replicas with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Read replicas",
  },
  {
    slug: "leader-follower-replication",
    caniuseFeature: "es6-module",
    title: "Leader-Follower replication",
    description:
      "A practical guide to Leader-Follower replication with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Leader-Follower replication",
  },
  {
    slug: "multi-leader-replication",
    caniuseFeature: "es6-module",
    title: "Multi-leader replication",
    description:
      "A practical guide to Multi-leader replication with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Multi-leader replication",
  },
  {
    slug: "quorum",
    caniuseFeature: "es6-module",
    title: "Quorum",
    description:
      "A practical guide to Quorum with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Quorum",
  },
  {
    slug: "consensus",
    caniuseFeature: "es6-module",
    title: "Consensus",
    description:
      "A practical guide to Consensus with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Consensus",
  },
  {
    slug: "cap-theorem",
    caniuseFeature: "es6-module",
    title: "CAP theorem",
    description:
      "A practical guide to CAP theorem with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn CAP theorem",
  },
  {
    slug: "base",
    caniuseFeature: "es6-module",
    title: "BASE",
    description:
      "A practical guide to BASE with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn BASE",
  },
  {
    slug: "acid",
    caniuseFeature: "es6-module",
    title: "ACID",
    description:
      "A practical guide to ACID with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn ACID",
  },
  {
    slug: "eventual-consistency",
    caniuseFeature: "es6-module",
    title: "Eventual consistency",
    description:
      "A practical guide to Eventual consistency with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Eventual consistency",
  },
  {
    slug: "strong-consistency",
    caniuseFeature: "es6-module",
    title: "Strong consistency",
    description:
      "A practical guide to Strong consistency with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Strong consistency",
  },
  {
    slug: "snapshot-isolation",
    caniuseFeature: "es6-module",
    title: "Snapshot isolation",
    description:
      "A practical guide to Snapshot isolation with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Snapshot isolation",
  },
  {
    slug: "mvcc-multi-version-concurrency-control",
    caniuseFeature: "es6-module",
    title: "MVCC (Multi-Version Concurrency Control)",
    description:
      "A practical guide to MVCC (Multi-Version Concurrency Control) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn MVCC (Multi-Version Concurrency Control)",
  },
  {
    slug: "two-phase-commit-2pc",
    caniuseFeature: "es6-module",
    title: "Two-phase commit (2PC)",
    description:
      "A practical guide to Two-phase commit (2PC) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Two-phase commit (2PC)",
  },
  {
    slug: "three-phase-commit-3pc",
    caniuseFeature: "es6-module",
    title: "Three-phase commit (3PC)",
    description:
      "A practical guide to Three-phase commit (3PC) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Three-phase commit (3PC)",
  },
  {
    slug: "write-ahead-logging-wal",
    caniuseFeature: "es6-module",
    title: "Write-ahead logging (WAL)",
    description:
      "A practical guide to Write-ahead logging (WAL) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Write-ahead logging (WAL)",
  },
  {
    slug: "checkpointing",
    caniuseFeature: "es6-module",
    title: "Checkpointing",
    description:
      "A practical guide to Checkpointing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Checkpointing",
  },
  {
    slug: "compaction",
    caniuseFeature: "es6-module",
    title: "Compaction",
    description:
      "A practical guide to Compaction with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Compaction",
  },
  {
    slug: "rebalancing",
    caniuseFeature: "es6-module",
    title: "Rebalancing",
    description:
      "A practical guide to Rebalancing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Rebalancing",
  },
  {
    slug: "resharding",
    caniuseFeature: "es6-module",
    title: "Resharding",
    description:
      "A practical guide to Resharding with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Resharding",
  },
  {
    slug: "data-locality",
    caniuseFeature: "es6-module",
    title: "Data locality",
    description:
      "A practical guide to Data locality with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Data locality",
  },
  {
    slug: "hot-partition",
    caniuseFeature: "es6-module",
    title: "Hot partition",
    description:
      "A practical guide to Hot partition with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Hot partition",
  },
  {
    slug: "split-brain",
    caniuseFeature: "es6-module",
    title: "Split-brain",
    description:
      "A practical guide to Split-brain with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Split-brain",
  },
  {
    slug: "failover",
    caniuseFeature: "es6-module",
    title: "Failover",
    description:
      "A practical guide to Failover with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Failover",
  },
  {
    slug: "high-availability-ha",
    caniuseFeature: "es6-module",
    title: "High availability (HA)",
    description:
      "A practical guide to High availability (HA) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn High availability (HA)",
  },
  {
    slug: "horizontal-scaling",
    caniuseFeature: "es6-module",
    title: "Horizontal scaling",
    description:
      "A practical guide to Horizontal scaling with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Horizontal scaling",
  },
  {
    slug: "vertical-scaling",
    caniuseFeature: "es6-module",
    title: "Vertical scaling",
    description:
      "A practical guide to Vertical scaling with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Vertical scaling",
  },
  {
    slug: "load-balancing",
    caniuseFeature: "es6-module",
    title: "Load balancing",
    description:
      "A practical guide to Load balancing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Load balancing",
  },
  {
    slug: "connection-pooling",
    caniuseFeature: "es6-module",
    title: "Connection pooling",
    description:
      "A practical guide to Connection pooling with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Connection pooling",
  },
  {
    slug: "caching",
    caniuseFeature: "es6-module",
    title: "Caching",
    description:
      "A practical guide to Caching with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Caching",
  },
  {
    slug: "materialized-views",
    caniuseFeature: "es6-module",
    title: "Materialized views",
    description:
      "A practical guide to Materialized views with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Materialized views",
  },
  {
    slug: "secondary-indexes",
    caniuseFeature: "es6-module",
    title: "Secondary indexes",
    description:
      "A practical guide to Secondary indexes with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Secondary indexes",
  },
  {
    slug: "composite-index",
    caniuseFeature: "es6-module",
    title: "Composite index",
    description:
      "A practical guide to Composite index with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Composite index",
  },
  {
    slug: "covering-index",
    caniuseFeature: "es6-module",
    title: "Covering index",
    description:
      "A practical guide to Covering index with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Covering index",
  },
  {
    slug: "bloom-filter",
    caniuseFeature: "es6-module",
    title: "Bloom filter",
    description:
      "A practical guide to Bloom filter with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Bloom filter",
  },
  {
    slug: "lsm-tree",
    caniuseFeature: "es6-module",
    title: "LSM tree",
    description:
      "A practical guide to LSM tree with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn LSM tree",
  },
  {
    slug: "b-tree",
    caniuseFeature: "es6-module",
    title: "B-tree",
    description:
      "A practical guide to B-tree with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn B-tree",
  },
  {
    slug: "query-planner",
    caniuseFeature: "es6-module",
    title: "Query planner",
    description:
      "A practical guide to Query planner with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Query planner",
  },
  {
    slug: "cost-based-optimizer",
    caniuseFeature: "es6-module",
    title: "Cost-based optimizer",
    description:
      "A practical guide to Cost-based optimizer with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Cost-based optimizer",
  },
  {
    slug: "deadlock",
    caniuseFeature: "es6-module",
    title: "Deadlock",
    description:
      "A practical guide to Deadlock with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Deadlock",
  },
  {
    slug: "lock-escalation",
    caniuseFeature: "es6-module",
    title: "Lock escalation",
    description:
      "A practical guide to Lock escalation with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Lock escalation",
  },
  {
    slug: "optimistic-locking",
    caniuseFeature: "es6-module",
    title: "Optimistic locking",
    description:
      "A practical guide to Optimistic locking with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Optimistic locking",
  },
  {
    slug: "pessimistic-locking",
    caniuseFeature: "es6-module",
    title: "Pessimistic locking",
    description:
      "A practical guide to Pessimistic locking with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Pessimistic locking",
  },
  {
    slug: "dirty-read",
    caniuseFeature: "es6-module",
    title: "Dirty read",
    description:
      "A practical guide to Dirty read with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Dirty read",
  },
  {
    slug: "phantom-read",
    caniuseFeature: "es6-module",
    title: "Phantom read",
    description:
      "A practical guide to Phantom read with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Phantom read",
  },
  {
    slug: "read-skew",
    caniuseFeature: "es6-module",
    title: "Read skew",
    description:
      "A practical guide to Read skew with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Read skew",
  },
  {
    slug: "write-skew",
    caniuseFeature: "es6-module",
    title: "Write skew",
    description:
      "A practical guide to Write skew with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Write skew",
  },
  {
    slug: "data-skew",
    caniuseFeature: "es6-module",
    title: "Data skew",
    description:
      "A practical guide to Data skew with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Data skew",
  },
  {
    slug: "backpressure",
    caniuseFeature: "es6-module",
    title: "Backpressure",
    description:
      "A practical guide to Backpressure with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Backpressure",
  },
  {
    slug: "circuit-breaker",
    caniuseFeature: "es6-module",
    title: "Circuit breaker",
    description:
      "A practical guide to Circuit breaker with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Circuit breaker",
  },
  {
    slug: "throttling",
    caniuseFeature: "es6-module",
    title: "Throttling",
    description:
      "A practical guide to Throttling with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Throttling",
  },
  {
    slug: "rate-limiting",
    caniuseFeature: "es6-module",
    title: "Rate limiting",
    description:
      "A practical guide to Rate limiting with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Rate limiting",
  },
  {
    slug: "cdc-change-data-capture",
    caniuseFeature: "es6-module",
    title: "CDC (Change Data Capture)",
    description:
      "A practical guide to CDC (Change Data Capture) with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn CDC (Change Data Capture)",
  },
  {
    slug: "logical-replication",
    caniuseFeature: "es6-module",
    title: "Logical replication",
    description:
      "A practical guide to Logical replication with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Logical replication",
  },
  {
    slug: "physical-replication",
    caniuseFeature: "es6-module",
    title: "Physical replication",
    description:
      "A practical guide to Physical replication with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Physical replication",
  },
  {
    slug: "geo-replication",
    caniuseFeature: "es6-module",
    title: "Geo-replication",
    description:
      "A practical guide to Geo-replication with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Geo-replication",
  },
  {
    slug: "federation",
    caniuseFeature: "es6-module",
    title: "Federation",
    description:
      "A practical guide to Federation with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Federation",
  },
  {
    slug: "data-lake",
    caniuseFeature: "es6-module",
    title: "Data lake",
    description:
      "A practical guide to Data lake with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Data lake",
  },
  {
    slug: "data-warehouse",
    caniuseFeature: "es6-module",
    title: "Data warehouse",
    description:
      "A practical guide to Data warehouse with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Data warehouse",
  },
  {
    slug: "columnar-storage",
    caniuseFeature: "es6-module",
    title: "Columnar storage",
    description:
      "A practical guide to Columnar storage with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Columnar storage",
  },
  {
    slug: "row-based-storage",
    caniuseFeature: "es6-module",
    title: "Row-based storage",
    description:
      "A practical guide to Row-based storage with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Row-based storage",
  },
  {
    slug: "time-series-partitioning",
    caniuseFeature: "es6-module",
    title: "Time-series partitioning",
    description:
      "A practical guide to Time-series partitioning with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Time-series partitioning",
  },
  {
    slug: "hash-partitioning",
    caniuseFeature: "es6-module",
    title: "Hash partitioning",
    description:
      "A practical guide to Hash partitioning with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Hash partitioning",
  },
  {
    slug: "range-partitioning",
    caniuseFeature: "es6-module",
    title: "Range partitioning",
    description:
      "A practical guide to Range partitioning with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Range partitioning",
  },
  {
    slug: "consistent-hashing",
    caniuseFeature: "es6-module",
    title: "Consistent hashing",
    description:
      "A practical guide to Consistent hashing with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Consistent hashing",
  },
  {
    slug: "data-migration",
    caniuseFeature: "es6-module",
    title: "Data migration",
    description:
      "A practical guide to Data migration with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Data migration",
  },
  {
    slug: "schema-evolution",
    caniuseFeature: "es6-module",
    title: "Schema evolution",
    description:
      "A practical guide to Schema evolution with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Schema evolution",
  },
  {
    slug: "schema-registry",
    caniuseFeature: "es6-module",
    title: "Schema registry",
    description:
      "A practical guide to Schema registry with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Schema registry",
  },
  {
    slug: "idempotency",
    caniuseFeature: "es6-module",
    title: "Idempotency",
    description:
      "A practical guide to Idempotency with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Idempotency",
  },
  {
    slug: "exactly-once-semantics",
    caniuseFeature: "es6-module",
    title: "Exactly-once semantics",
    description:
      "A practical guide to Exactly-once semantics with mental models, implementation patterns, and common production pitfalls.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Distributed Systems", "Reliability"],
    readTime: "8 min read",
    cursorText: "🗄️ Learn Exactly-once semantics",
  },
  {
    slug: "css-grid-lanes",
    caniuseFeature: "css-grid-lanes",
    title: "CSS Grid Lanes: The New Name for CSS Masonry",
    description:
      "CSS masonry has been renamed to grid-lanes in the spec. Safari is leading the charge — here's what it is, how to use it today, and what to watch as the standard evolves.",
    date: "2026-02-22",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Grid", "Layout"],
    readTime: "6 min read",
    cursorText: "🧱 Explore Grid Lanes",
  },
  {
    slug: "react-reconciliation",
    caniuseFeature: "es6-module",
    title: "React Reconciliation Algorithm",
    description:
      "How React compares old and new element trees to compute the minimal set of DOM mutations — and what the O(n) heuristics mean for your component structure.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Internals"],
    readTime: "6 min read",
    cursorText: "⚛️ Learn Reconciliation",
  },
  {
    slug: "virtual-dom-diffing",
    caniuseFeature: "es6-module",
    title: "Virtual DOM Diffing",
    description:
      "What the virtual DOM actually is, how React's diffing algorithm compares two trees, and when the vdom overhead is — and isn't — worth it.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Internals"],
    readTime: "6 min read",
    cursorText: "⚛️ Understand vDOM",
  },
  {
    slug: "react-fiber-architecture",
    caniuseFeature: "es6-module",
    title: "React Fiber Architecture",
    description:
      "How React's Fiber reconciler replaced the stack-based reconciler to make rendering interruptible, prioritizable, and resumable.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Internals"],
    readTime: "7 min read",
    cursorText: "⚛️ Explore Fiber",
  },
  {
    slug: "concurrent-rendering",
    caniuseFeature: "es6-module",
    title: "Concurrent Rendering",
    description:
      "How React 18's concurrent renderer lets React pause, resume, and prioritize rendering — and what that means for your app.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "React 18"],
    readTime: "6 min read",
    cursorText: "⚛️ Concurrent Mode",
  },
  {
    slug: "automatic-batching-react-18",
    caniuseFeature: "es6-module",
    title: "Automatic Batching (React 18)",
    description:
      "React 18 batches state updates from all origins — not just event handlers. What changed, why it matters, and how to opt out with flushSync.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "React 18", "Performance"],
    readTime: "5 min read",
    cursorText: "⚛️ Batching Deep Dive",
  },
  {
    slug: "start-transition",
    caniuseFeature: "es6-module",
    title: "startTransition and useTransition",
    description:
      "Mark state updates as non-urgent so React can keep user input responsive while heavy renders happen in the background.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "React 18", "Performance"],
    readTime: "5 min read",
    cursorText: "⚛️ Transitions",
  },
  {
    slug: "suspense-boundaries",
    caniuseFeature: "es6-module",
    title: "Suspense Boundaries",
    description:
      "Declaratively specify loading states for async parts of your component tree — how Suspense works, where to place boundaries, and how it integrates with concurrent mode.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Data Fetching", "Architecture"],
    readTime: "6 min read",
    cursorText: "⚛️ Suspense",
  },
  {
    slug: "error-boundaries",
    caniuseFeature: "es6-module",
    title: "Error Boundaries",
    description:
      "Catch render errors in child trees and show fallback UI instead of crashing the whole app — how to define them, what they catch, and how to reset.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Error Handling", "Architecture"],
    readTime: "5 min read",
    cursorText: "⚛️ Error Boundaries",
  },
  {
    slug: "synthetic-event-system",
    caniuseFeature: "es6-module",
    title: "Synthetic Event System",
    description:
      "How React normalizes browser events, why it uses event delegation at the root, and what changed with event pooling in React 17.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Events", "Internals"],
    readTime: "5 min read",
    cursorText: "⚛️ Events",
  },
  {
    slug: "keys-and-list-rerender",
    caniuseFeature: "es6-module",
    title: "Keys and List Re-render Behavior",
    description:
      "How React uses keys to match elements across renders, when index keys cause bugs, and how to force remounting with key changes.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Lists"],
    readTime: "5 min read",
    cursorText: "⚛️ Keys",
  },
  {
    slug: "memoization-react",
    caniuseFeature: "es6-module",
    title: "Memoization: React.memo, useMemo, useCallback",
    description:
      "When and how to use React's memoization tools to skip re-renders and expensive recomputations — and when not to bother.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Hooks"],
    readTime: "6 min read",
    cursorText: "⚛️ Memoization",
  },
  {
    slug: "stale-closures",
    caniuseFeature: "es6-module",
    title: "Stale Closures in React",
    description:
      "What stale closures are, why they're so common in React hooks, and the three patterns that reliably fix them.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Hooks", "Debugging"],
    readTime: "5 min read",
    cursorText: "⚛️ Stale Closures",
  },
  {
    slug: "dependency-array-pitfalls",
    caniuseFeature: "es6-module",
    title: "Dependency Array Pitfalls",
    description:
      "The most common mistakes in useEffect, useMemo, and useCallback dependency arrays — missing deps, object/function instability, and when to suppress the lint rule.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Hooks", "Debugging"],
    readTime: "5 min read",
    cursorText: "⚛️ Deps Array",
  },
  {
    slug: "custom-hooks-design-patterns",
    caniuseFeature: "es6-module",
    title: "Custom Hooks Design Patterns",
    description:
      "How to extract, compose, and structure custom hooks — return shapes, callback stabilization, subscription patterns, and testing strategies.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Hooks", "Patterns"],
    readTime: "6 min read",
    cursorText: "⚛️ Custom Hooks",
  },
  {
    slug: "context-api-rerender-optimization",
    caniuseFeature: "es6-module",
    title: "Context API Re-render Optimization",
    description:
      "Why context consumers re-render on every value change, and four patterns to minimize the performance impact — split contexts, memoized values, dispatch separation, and selectors.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Context"],
    readTime: "6 min read",
    cursorText: "⚛️ Context",
  },
  {
    slug: "prop-drilling-vs-composition",
    caniuseFeature: "es6-module",
    title: "Prop Drilling vs Composition",
    description:
      "How component composition eliminates prop drilling before you reach for context — and when context is actually the right tool.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Architecture", "Patterns"],
    readTime: "5 min read",
    cursorText: "⚛️ Composition",
  },
  {
    slug: "render-props-pattern",
    caniuseFeature: "es6-module",
    title: "Render Props Pattern",
    description:
      "How render props share behavior between components, common use cases, and why custom hooks replaced most render prop patterns.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Patterns", "Architecture"],
    readTime: "5 min read",
    cursorText: "⚛️ Render Props",
  },
  {
    slug: "higher-order-components",
    caniuseFeature: "es6-module",
    title: "Higher-Order Components (HOC)",
    description:
      "The HOC pattern, its classic problems (wrapper hell, prop collision, ref loss), and how to migrate to hooks for new code.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Patterns", "Architecture"],
    readTime: "5 min read",
    cursorText: "⚛️ HOCs",
  },
  {
    slug: "forward-refs",
    caniuseFeature: "es6-module",
    title: "Forward Refs",
    description:
      "How forwardRef exposes DOM nodes through the component boundary, when to use it, and what changes in React 19.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Refs", "Patterns"],
    readTime: "4 min read",
    cursorText: "⚛️ forwardRef",
  },
  {
    slug: "use-imperative-handle",
    caniuseFeature: "es6-module",
    title: "useImperativeHandle",
    description:
      "Expose a controlled imperative API to parent components via refs — without giving them unrestricted access to the underlying DOM node.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Refs", "Hooks"],
    readTime: "4 min read",
    cursorText: "⚛️ Imperative Handle",
  },
  {
    slug: "strict-mode",
    caniuseFeature: "es6-module",
    title: "StrictMode",
    description:
      "What React's StrictMode actually does — double-invoking renders and effects, detecting deprecated APIs, and the bugs it's designed to surface.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Debugging", "Development"],
    readTime: "5 min read",
    cursorText: "⚛️ StrictMode",
  },
  {
    slug: "hydration-ssr",
    caniuseFeature: "es6-module",
    title: "Hydration in SSR",
    description:
      "How React attaches to server-rendered HTML, what causes hydration mismatches, and how React 18's streaming SSR changes the picture.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "SSR", "Rendering"],
    readTime: "6 min read",
    cursorText: "⚛️ Hydration",
  },
  {
    slug: "csr-vs-ssr-vs-ssg",
    caniuseFeature: "es6-module",
    title: "CSR vs SSR vs SSG",
    description:
      "The trade-offs between client-side rendering, server-side rendering, and static generation — performance, SEO, cost, and when to use each.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Rendering", "Architecture"],
    readTime: "6 min read",
    cursorText: "⚛️ Rendering Strategies",
  },
  {
    slug: "server-components",
    caniuseFeature: "es6-module",
    title: "React Server Components",
    description:
      "Server Components run exclusively on the server, have zero bundle cost, and can fetch data directly — here's the mental model and what they can and can't do.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "SSR", "Architecture"],
    readTime: "6 min read",
    cursorText: "⚛️ Server Components",
  },
  {
    slug: "code-splitting-react-lazy",
    caniuseFeature: "es6-module",
    title: "Code Splitting with React.lazy",
    description:
      "How React.lazy and dynamic imports split your bundle by route or feature, with preloading strategies and guidance on what to split.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Bundling"],
    readTime: "5 min read",
    cursorText: "⚛️ Code Splitting",
  },
  {
    slug: "tree-shaking",
    caniuseFeature: "es6-module",
    title: "Tree Shaking",
    description:
      "How bundlers eliminate unused exports, what breaks tree shaking (barrel files, CommonJS, missing sideEffects field), and how to verify it's working.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Bundling"],
    readTime: "5 min read",
    cursorText: "⚛️ Tree Shaking",
  },
  {
    slug: "state-colocation",
    caniuseFeature: "es6-module",
    title: "State Colocation Strategy",
    description:
      "Put state as close to where it's used as possible — the colocation decision tree, derived state anti-patterns, and grouping state in custom hooks.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "State Management", "Architecture"],
    readTime: "5 min read",
    cursorText: "⚛️ Colocation",
  },
  {
    slug: "lifting-state-up",
    caniuseFeature: "es6-module",
    title: "Lifting State Up — Trade-offs",
    description:
      "When to lift state to a common ancestor, the re-render and coupling costs it introduces, and when to stop lifting and reach for context or a store.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "State Management", "Architecture"],
    readTime: "5 min read",
    cursorText: "⚛️ Lift State",
  },
  {
    slug: "immutability-structural-sharing",
    caniuseFeature: "es6-module",
    title: "Immutability and Structural Sharing",
    description:
      "Why React requires immutable state updates, how structural sharing makes them efficient, and when Immer is the right tool for nested updates.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "State Management", "Patterns"],
    readTime: "5 min read",
    cursorText: "⚛️ Immutability",
  },
  {
    slug: "use-reducer-patterns",
    caniuseFeature: "es6-module",
    title: "Reducer Patterns with useReducer",
    description:
      "When useReducer beats useState, action creator patterns, Immer integration, and sharing dispatch via context for app-level state.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "State Management", "Hooks"],
    readTime: "6 min read",
    cursorText: "⚛️ useReducer",
  },
  {
    slug: "optimistic-ui-updates",
    caniuseFeature: "es6-module",
    title: "Optimistic UI Updates",
    description:
      "Apply changes to the UI before the server confirms them, then roll back on failure — the pattern, React 19's useOptimistic, and TanStack Query integration.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "UX", "Data Fetching"],
    readTime: "5 min read",
    cursorText: "⚛️ Optimistic UI",
  },
  {
    slug: "abort-controller",
    caniuseFeature: "abortcontroller",
    title: "AbortController in React",
    description:
      "Cancel in-flight fetch requests in useEffect cleanup to prevent state updates on unmounted components and handle fast dependency changes.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Data Fetching", "Hooks"],
    readTime: "5 min read",
    cursorText: "⚛️ AbortController",
  },
  {
    slug: "use-effect-cleanup-timing",
    caniuseFeature: "es6-module",
    title: "Cleanup Function Timing in useEffect",
    description:
      "When cleanup runs, how StrictMode's double-mount exercises it, and the right patterns for async effects and every common resource type.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Hooks", "Lifecycle"],
    readTime: "5 min read",
    cursorText: "⚛️ Effect Cleanup",
  },
  {
    slug: "use-layout-effect",
    caniuseFeature: "es6-module",
    title: "useLayoutEffect vs useEffect",
    description:
      "The timing difference between useLayoutEffect and useEffect, when to use each, and how to handle the SSR warning.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Hooks", "Performance"],
    readTime: "4 min read",
    cursorText: "⚛️ Layout Effect",
  },
  {
    slug: "microtask-vs-macrotask",
    caniuseFeature: "es6-module",
    title: "Microtasks vs Macrotasks in React",
    description:
      "How JavaScript's event loop task queues interact with React's scheduler, batching, and effect timing.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "JavaScript", "Performance"],
    readTime: "5 min read",
    cursorText: "⚛️ Task Queues",
  },
  {
    slug: "render-bailout-conditions",
    caniuseFeature: "es6-module",
    title: "Bailout Conditions in Rendering",
    description:
      "How React skips re-renders via useState same-value bailout, React.memo, shouldComponentUpdate, and memoized values — and what defeats each.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Internals"],
    readTime: "5 min read",
    cursorText: "⚛️ Bailout",
  },
  {
    slug: "react-devtools-profiler",
    caniuseFeature: "es6-module",
    title: "React DevTools Profiler",
    description:
      "How to record and read profiles, interpret flamegraphs, use the ranked chart, and enable why-did-this-render to find performance bottlenecks.",
    date: "2026-02-23",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["React", "Performance", "Debugging"],
    readTime: "6 min read",
    cursorText: "⚛️ Profiler",
  },
  {
    slug: "glm5-zed-agentic-setup",
    caniuseFeature: "es6-module",
    title: "GLM-5 on Zed: Full Agentic Setup",
    description:
      "How to configure Z.ai's GLM-5 coding plan in Zed so the agent can actually read your files, search your codebase, and complete multi-step tasks.",
    date: "2026-02-24",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["AI", "Zed", "Developer Tools", "Agents"],
    readTime: "5 min read",
    cursorText: "🤖 Configure GLM-5 Agent",
  },
  {
    slug: "p3-color-gamut",
    caniuseFeature: "css-color-function",
    caniuseFeature2: "css-color-mix",
    title: "P3 Color Gamut: The Most Vibrant Color Model",
    description:
      "How to use display-p3 colors in CSS with proper fallbacks, smart CSS variable upgrades, and OKLCH for perceptually uniform wide-gamut color.",
    date: "2026-02-24",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Color", "Design"],
    readTime: "8 min read",
    cursorText: "🎨 P3 Color",
  },
  {
    slug: "css-at-property",
    caniuseFeature: "es6-module",
    title: "@property: Animating CSS Custom Properties",
    description:
      "How the CSS @property rule unlocks smooth animation of custom properties by giving the browser the type information it needs to interpolate values.",
    date: "2026-02-24",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Animation", "Houdini"],
    readTime: "7 min read",
    cursorText: "🎞️ Register & Animate",
  },
  {
    slug: "css-anchor-scope",
    caniuseFeature: "css-anchor-positioning",
    title: "CSS anchor-scope: Scoped Anchor Positioning",
    description:
      "anchor-scope prevents anchor name collisions in repeating components — the missing piece that makes CSS anchor positioning safe for design systems.",
    date: "2026-02-24",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Layout", "UI"],
    readTime: "6 min read",
    cursorText: "📌 Scope Your Anchors",
  },
  {
    slug: "fit-text",
    caniuseFeature: "css-container-queries",
    title: "Fitting Text to Full Viewport Width",
    description:
      "Every technique for making a heading fill its container — vw, cqi, clamp(), SVG textLength, binary search JS, and scaleX — with a performance comparison.",
    date: "2026-02-26",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Typography", "Performance"],
    readTime: "7 min read",
    cursorText: "↔ Fill the Width",
  },
  {
    slug: "tanstack",
    caniuseFeature: "es6-module",
    title: "Everything to Know About TanStack",
    description:
      "A complete guide to the TanStack ecosystem — Query, Router, Table, Form, Virtual, and Store — with practical patterns and TypeScript examples.",
    date: "2026-02-26",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["JavaScript", "React", "Architecture"],
    readTime: "10 min read",
    cursorText: "🗃️ Explore TanStack",
  },
  {
    slug: "scroll-animations",
    caniuseFeature: "wf-scroll-driven-animations",
    title: "Scroll-Driven Animations",
    description:
      "Use scroll() and view() timelines for progress bars, reveals, and scrubbed motion that stay tied to scroll position instead of scroll event handlers.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Animation", "Scroll"],
    readTime: "8 min read",
    cursorText: "↕️ Drive Motion by Scroll",
  },
  {
    slug: "broadcast-channel-api",
    caniuseFeature: "broadcastchannel",
    title: "Broadcast Channel API for Cross-Tab Sync",
    description:
      "How to use BroadcastChannel to sync browser tabs and windows in realtime with operation-based messaging, hydration, and compatibility fallbacks.",
    date: "2026-02-26",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Web APIs", "Realtime", "Collaboration"],
    readTime: "6 min read",
    cursorText: "📡 Sync Tabs",
  },
  {
    slug: "light-dark-mode-made-simple",
    caniuseFeature: "wf-light-dark",
    caniuseEmbedFeature: "prefers-color-scheme",
    title: "Light & Dark Mode Made Simple",
    description:
      "Stop duplicating dark-mode CSS. Use light-dark() to define light and dark values in one token and keep theme variables in one place.",
    date: "2026-02-26",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Theming", "Design Systems"],
    readTime: "5 min read",
    cursorText: "🌗 One-Line Theme Tokens",
  },
  {
    slug: "warrior-of-the-gods-easter-egg",
    caniuseFeature: "canvas",
    title: "Building Warrior of the Gods",
    description:
      "How we built a full Norse mythology side-scrolling platformer as a hidden easter egg in a SvelteKit blog — canvas primitives, Web Audio synthesis, Ragnar transformation, and Konami code discovery.",
    date: "2026-03-07",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Canvas", "Game Dev", "SvelteKit", "Web Audio"],
    readTime: "9 min read",
    cursorText: "⚔ Enter Valhalla",
  },
  {
    slug: "tldraw",
    caniuseFeature: "pointer-events",
    title: "tldraw",
    description:
      "Install tldraw, wire it to your backend with snapshots and store subscriptions, read and push shape data as domain objects, and control the store directly for multiplayer.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Canvas", "React", "Open Source", "Collaboration"],
    readTime: "10 min read",
    cursorText: "🎨 Draw Infinitely",
  },
  {
    slug: "api-gateway",
    caniuseFeature: "es6-module",
    title: "API Gateway",
    description:
      "Route requests through a single policy layer for auth, quotas, observability, and upstream isolation without pushing business logic into the edge.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Architecture", "API Design", "Operations"],
    readTime: "6 min read",
    cursorText: "🚪 API Gateway",
  },
  {
    slug: "load-balancer",
    caniuseFeature: "es6-module",
    title: "Load Balancer",
    description:
      "Distribute traffic by actual capacity and connection behavior, not by round-robin hope.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Networking", "Reliability", "Infrastructure"],
    readTime: "5 min read",
    cursorText: "⚖️ Balance Traffic",
  },
  {
    slug: "reverse-proxy",
    caniuseFeature: "es6-module",
    title: "Reverse Proxy",
    description:
      "Terminate client traffic at a controlled edge layer for TLS, headers, compression, and upstream shielding.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Networking", "Security", "Infrastructure"],
    readTime: "5 min read",
    cursorText: "↩️ Reverse Proxy",
  },
  {
    slug: "pagination",
    caniuseFeature: "es6-module",
    title: "Pagination",
    description:
      "Page through large mutable datasets with stable ordering, predictable latency, and cursors that survive growth.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "API Design", "Performance"],
    readTime: "6 min read",
    cursorText: "📚 Page Results",
  },
  {
    slug: "cache-stampede",
    caniuseFeature: "es6-module",
    title: "Cache Stampede",
    description:
      "Prevent hot-key misses from turning one expired entry into a thundering herd against your database.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Caching", "Reliability", "Performance"],
    readTime: "6 min read",
    cursorText: "🔥 Stop Stampedes",
  },
  {
    slug: "graphql",
    caniuseFeature: "es6-module",
    title: "GraphQL",
    description:
      "Expose a typed graph clients can query precisely while keeping resolver costs and N+1 explosions under control.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["APIs", "Schema Design", "Backend"],
    readTime: "7 min read",
    cursorText: "🔍 Query Precisely",
  },
  {
    slug: "grpc",
    caniuseFeature: "es6-module",
    title: "gRPC",
    description:
      "Use protobuf contracts, deadlines, and streaming to make internal service calls faster and harder to drift.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["APIs", "Backend", "Distributed Systems"],
    readTime: "6 min read",
    cursorText: "📡 Typed RPC",
  },
  {
    slug: "webhooks",
    caniuseFeature: "es6-module",
    title: "Webhooks",
    description:
      "Deliver outbound events reliably with signatures, retries, idempotency, and explicit failure handling.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["APIs", "Integration", "Reliability"],
    readTime: "6 min read",
    cursorText: "🪝 Push Events",
  },
  {
    slug: "jwt",
    caniuseFeature: "es6-module",
    title: "JWT",
    description:
      "Verify signed claims cheaply, but keep authorization freshness and revocation outside the token.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Authentication", "APIs"],
    readTime: "6 min read",
    cursorText: "🔐 Signed Claims",
  },
  {
    slug: "oauth",
    caniuseFeature: "es6-module",
    title: "OAuth",
    description:
      "Delegate access safely with the authorization code flow, PKCE, and strict redirect and state validation.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Authentication", "APIs"],
    readTime: "7 min read",
    cursorText: "🔑 Delegated Access",
  },
  {
    slug: "cache-invalidation",
    caniuseFeature: "es6-module",
    title: "Cache Invalidation",
    description:
      "Keep cached views fresh by designing keys, tags, TTLs, and write paths together instead of as separate concerns.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Caching", "Data Consistency", "Performance"],
    readTime: "6 min read",
    cursorText: "♻️ Fresh or Stale",
  },
  {
    slug: "query-optimization",
    caniuseFeature: "es6-module",
    title: "Query Optimization",
    description:
      "Tune the planner with better predicates, indexes, and query shape before reaching for hints.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "SQL", "Performance"],
    readTime: "6 min read",
    cursorText: "🧠 Tune Queries",
  },
  {
    slug: "sharding",
    caniuseFeature: "es6-module",
    title: "Sharding",
    description:
      "Split hot datasets across nodes without losing routing clarity, rebalance safety, or tenant isolation.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Databases", "Scalability", "Distributed Systems"],
    readTime: "7 min read",
    cursorText: "🪓 Split the Data",
  },
  {
    slug: "livelock",
    caniuseFeature: "es6-module",
    title: "Livelock",
    description:
      "Detect systems that keep retrying, backing off, and yielding without ever making forward progress.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Concurrency", "Reliability", "Systems"],
    readTime: "5 min read",
    cursorText: "🏃 No Progress",
  },
  {
    slug: "csrf",
    caniuseFeature: "same-site-cookie-attribute",
    title: "CSRF",
    description:
      "Stop cross-site request forgery by verifying origin and user intent instead of trusting ambient cookies.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Web", "Authentication"],
    readTime: "6 min read",
    cursorText: "🛡️ Verify Intent",
  },
  {
    slug: "false-sharing",
    caniuseFeature: "sharedarraybuffer",
    title: "False Sharing",
    description:
      "Avoid cache-line contention between independent workers that silently destroys multicore throughput.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Concurrency", "Performance", "Systems"],
    readTime: "5 min read",
    cursorText: "🧵 Cache-Line Clash",
  },
  {
    slug: "mtls",
    caniuseFeature: "es6-module",
    title: "mTLS",
    description:
      "Use mutual TLS to give services cryptographic identity on every connection, not just encrypted transport.",
    date: "2026-03-09",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Security", "Networking", "Infrastructure"],
    readTime: "6 min read",
    cursorText: "🤝 Mutual TLS",
  },
  {
    slug: "css-attr-function",
    caniuseFeature: "css3-attr",
    title: "CSS attr() Function Improvements",
    description:
      "Use typed attr() values as local component inputs so HTML attributes can drive spacing, sizing, and variants directly from CSS.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Functions", "Design Systems"],
    readTime: "6 min read",
    cursorText: "🧮 CSS Props via attr()",
  },
  {
    slug: "css-zoom",
    caniuseFeature: "css-zoom",
    title: "CSS zoom",
    description:
      "Use layout-aware zoom for editor canvases and previews when scale() is the wrong tool, while keeping fallback paths explicit.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Layout", "Accessibility"],
    readTime: "5 min read",
    cursorText: "🔎 Layout-Aware Zoom",
  },
  {
    slug: "css-custom-highlight-api",
    caniuseFeature: "wf-custom-highlight",
    title: "CSS Custom Highlight API",
    description:
      "Highlight search hits, comments, and collaboration ranges without wrapping text in extra spans by using named browser-managed highlights.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Selection", "Editing"],
    readTime: "6 min read",
    cursorText: "🖍️ Paint Text Ranges",
  },
  {
    slug: "navigation-api",
    caniuseFeature: "wf-navigation",
    title: "Navigation API",
    description:
      "Intercept navigations with browser-native history semantics so routers, transitions, and scroll restoration stop fighting each other.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Navigation", "Browser APIs", "Performance"],
    readTime: "7 min read",
    cursorText: "🧭 Intercept Navigation",
  },
  {
    slug: "media-state-pseudo-classes",
    caniuseFeature: "mdn-css_selectors_playing",
    caniuseFeature2: "mdn-css_selectors_muted",
    title: "Media State Pseudo-Classes",
    description:
      "Style audio and video UI directly from player state with selectors like :playing, :paused, :buffering, and :muted instead of mirroring state into JS classes.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Media", "Video"],
    readTime: "6 min read",
    cursorText: "🎵 Style Media State",
  },
  {
    slug: "css-shape",
    caniuseFeature: "css-clip-path",
    title: "CSS Shapes with clip-path",
    description:
      "Use clip-path and shape-driven masking to build sharper hero treatments, avatars, and reveals without exporting bespoke raster assets.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "CSS", "Graphics", "Layout"],
    readTime: "6 min read",
    cursorText: "✂️ Cut with clip-path",
  },
  {
    slug: "web-compat",
    caniuseFeature: "es6-module",
    title: "Web Compatibility",
    description:
      "Interop work is not only new APIs: module loading, scroll timing, and long-standing prefixed quirks still decide whether sites behave the same across engines.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Web Compatibility", "Browser Engines", "Platform"],
    readTime: "7 min read",
    cursorText: "🧩 Fix Browser Gaps",
  },
  {
    slug: "webtransport",
    caniuseFeature: "webtransport",
    title: "WebTransport",
    description:
      "Use HTTP/3 streams, datagrams, and connection migration for realtime traffic that outgrows WebSockets but still needs a browser-friendly API.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Networking", "Realtime", "HTTP/3"],
    readTime: "7 min read",
    cursorText: "🚄 Realtime over HTTP/3",
  },
  {
    slug: "jpeg-xl",
    caniuseFeature: "jpegxl",
    title: "JPEG XL",
    description:
      "JPEG XL brings alpha, animation, lossless workflows, and high-fidelity compression to modern image pipelines that still need sane fallbacks.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Images", "Compression", "Media"],
    readTime: "6 min read",
    cursorText: "🖼️ Explore JPEG XL",
  },
  {
    slug: "webvtt",
    caniuseFeature: "webvtt",
    title: "WebVTT",
    description:
      "Use WebVTT for captions, chapters, metadata cues, and synchronized text tracks instead of treating it as subtitles-only plumbing.",
    date: "2026-03-10",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Interop 2026", "Media", "Accessibility", "Video"],
    readTime: "5 min read",
    cursorText: "💬 Track Timed Text",
  },
  {
    slug: "vite-plus-and-voidzero",
    caniuseFeature: "es6-module",
    title: "Vite+ and VoidZero",
    description:
      "Vite+ turns VoidZero's tooling stack into one `vp`-driven workflow for installs, dev, checks, tests, builds, and monorepo tasks.",
    date: "2026-03-31",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["JavaScript Tooling", "Vite", "VoidZero", "Build Tools"],
    readTime: "6 min read",
    cursorText: "⚡ One CLI for JS",
  },
  {
    slug: "pretext",
    caniuseFeature: "mdn-api_textmetrics",
    hideCaniuseSupport: true,
    title: "Pretext: Text Layout Without DOM Measurement",
    description:
      "Pretext precomputes multilingual text measurement so you can ask for wrapped height and line ranges without touching the DOM or triggering layout reflow.",
    date: "2026-04-05",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["JavaScript", "Typography", "Performance", "Canvas"],
    readTime: "6 min read",
    cursorText: "📐 Measure Text Fast",
  },
  {
    slug: "html-in-canvas",
    caniuseFeature: "canvas",
    title: "HTML in Canvas",
    description:
      "Render styled HTML markup onto a canvas element today using SVG foreignObject, and get ready for the native drawHTMLElement() method coming to the Canvas 2D spec.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Canvas", "JavaScript", "HTML", "Web APIs"],
    readTime: "5 min read",
    cursorText: "🎨 HTML → Canvas",
  },
  {
    slug: "css-masonry-grid-lanes",
    caniuseFeature: "css-grid",
    title: "CSS Masonry — CSS Grid Lanes",
    description:
      "Native CSS masonry via grid-template-rows: masonry lands in Interop 2026, replacing JavaScript layout engines with a single CSS property.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Grid", "Layout", "Interop 2026"],
    readTime: "5 min read",
    cursorText: "🧱 Native Masonry",
  },
  {
    slug: "container-queries-scoped-css",
    caniuseFeature: "css-container-queries",
    title: "Container Queries — Another Way to Scope CSS Natively",
    description:
      "Named @container rules aren't just for responsive components — they're a practical CSS scoping boundary that requires no Shadow DOM or class namespacing.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Container Queries", "Scoping", "Layout"],
    readTime: "5 min read",
    cursorText: "📦 Scope with @container",
  },
  {
    slug: "css-scope",
    caniuseFeature: "css-cascade-scope",
    title: "@scope CSS",
    description:
      "@scope limits where CSS rules apply in the DOM — native scoping without build tools, Shadow DOM, or BEM class naming conventions.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Scoping", "Cascade"],
    readTime: "5 min read",
    cursorText: "🎯 Scope Your CSS",
  },
  {
    slug: "css-style-queries",
    caniuseFeature: "css-container-queries-style",
    title: "Style Queries Coming to Container Queries",
    description:
      "Style queries let @container respond to CSS custom property values — not just dimensions — enabling token-driven component variants without JavaScript.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Container Queries", "Design Tokens", "Interop 2026"],
    readTime: "5 min read",
    cursorText: "🎨 Style Queries",
  },
  {
    slug: "haptic-feedback-vibration-api",
    caniuseFeature: "vibration",
    title: "Haptic Feedback API",
    description:
      "The Vibration API delivers tactile feedback on mobile with a single method call — pulse patterns, drag feedback, and long-press cues without any native bridge.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Web APIs", "Mobile", "PWA", "UX"],
    readTime: "4 min read",
    cursorText: "📳 Feel the Feedback",
  },
  {
    slug: "css-random",
    caniuseFeature: "es6-module",
    title: "CSS random()",
    description:
      "The CSS random() function generates controlled randomness directly in stylesheets — per-element or shared, with optional step quantization.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "CSS Values", "Generative", "Experimental"],
    readTime: "4 min read",
    cursorText: "🎲 CSS Randomness",
  },
  {
    slug: "search-text-pseudo-class",
    caniuseFeature: "es6-module",
    title: "::search-text and :current Pseudo-Classes",
    description:
      "Style find-in-page highlights with ::search-text and mark the active match with :current — replacing the browser's default yellow with your own design tokens.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Pseudo-Elements", "Highlight API", "Experimental"],
    readTime: "4 min read",
    cursorText: "🔍 Style Search Hits",
  },
  {
    slug: "css-sticky-x-and-y",
    caniuseFeature: "css-sticky",
    title: "Sticky Both Ways — X and Y Axis",
    description:
      "position: sticky now works on the inline axis too — combine top and left offsets to freeze table headers, column labels, and UI chrome in two dimensions.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Layout", "Scroll", "Tables"],
    readTime: "4 min read",
    cursorText: "📌 Stick in 2D",
  },
  {
    slug: "css-multicolumn-wrap",
    caniuseFeature: "multicolumn",
    title: "Multi-Column Wrap and Height Properties",
    description:
      "column-wrap and column-height bring proper row-wrapping and height limits to CSS multi-column layout — magazine-style pagination without JavaScript.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Layout", "Multi-Column", "Typography"],
    readTime: "4 min read",
    cursorText: "📰 Column Control",
  },
  {
    slug: "css-border-shape",
    caniuseFeature: "es6-module",
    title: "CSS border-shape",
    description:
      "border-shape reshapes box geometry with a single keyword — squircles, superellipses, notches, scoops, and bevels in pure CSS without SVG or clip-path strings.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Borders", "Shapes", "Experimental"],
    readTime: "4 min read",
    cursorText: "🔷 Shape Any Border",
  },
  {
    slug: "css-border-image",
    caniuseFeature: "border-image",
    title: "CSS border-image",
    description:
      "border-image replaces standard borders with sliced images or gradients — the key to gradient borders, animated outlines, and decorative frames without extra DOM elements.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Borders", "Gradients", "Visual"],
    readTime: "5 min read",
    cursorText: "🖼️ Image Borders",
  },
  {
    slug: "element-scoped-view-transitions",
    caniuseFeature: "view-transitions",
    title: "Element-Scoped View Transitions",
    description:
      "element.startViewTransition() runs smooth snapshot crossfades on a single DOM subtree — list reorders, tab swaps, and card mutations without touching the rest of the page.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "View Transitions", "Animation", "JavaScript"],
    readTime: "5 min read",
    cursorText: "✨ Scoped Transitions",
  },
  {
    slug: "img-sizes-auto",
    caniuseFeature: "mdn-html_elements_img_sizes_auto",
    title: 'sizes="auto" on Images',
    description:
      'sizes="auto" lets the browser measure a lazy image\'s rendered width itself — no manual breakpoint math, no fragile sizes strings to maintain.',
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["HTML", "Images", "Performance", "Responsive"],
    readTime: "4 min read",
    cursorText: "🖼️ Auto-Sized Images",
  },
  {
    slug: "light-dark-image-support",
    caniuseFeature: "css-light-dark",
    title: "light-dark() Image Support",
    description:
      "The light-dark() function now accepts image values — swap photos, SVGs, and gradients between color schemes with a single CSS declaration.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Dark Mode", "Images", "Color"],
    readTime: "4 min read",
    cursorText: "🌗 Theme-Aware Images",
  },
  {
    slug: "css-revert-rule",
    caniuseFeature: "css-revert-layer",
    title: "CSS revert-layer",
    description:
      "revert-layer rolls a property back to its previous cascade layer value — the surgical undo that revert and unset can't provide when working with @layer.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Cascade", "Layers", "Architecture"],
    readTime: "4 min read",
    cursorText: "↩️ Layer Undo",
  },
  {
    slug: "aria-notify",
    caniuseFeature: "es6-module",
    title: "ariaNotify()",
    description:
      "ariaNotify() sends accessible announcements directly to screen readers — no live region markup, no invisible DOM nodes, no requestAnimationFrame timing hacks.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["Accessibility", "ARIA", "JavaScript", "Web APIs"],
    readTime: "4 min read",
    cursorText: "♿ Announce Anything",
  },
  {
    slug: "media-pseudo-classes",
    caniuseFeature: "css-media-interaction",
    title: "Media Pseudo-Classes — :playing, :paused",
    description:
      "CSS :playing, :paused, and :muted let you style media elements based on playback state — no JavaScript event listeners or manual class toggling needed.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Media", "Pseudo-Classes", "Video"],
    readTime: "4 min read",
    cursorText: "▶️ Style Playback State",
  },
  {
    slug: "scroll-driven-variable-fonts",
    caniuseFeature: "css-scroll-driven-animations",
    title: "Scroll-Driven Variable Fonts",
    description:
      "Wire variable font axes directly to scroll position using animation-timeline — weight, width, and custom axes that respond to scroll with zero JavaScript.",
    date: "2026-05-08",
    author: "@dinglestein",
    authorLink: "https://x.com/dinglestein",
    tags: ["CSS", "Typography", "Scroll", "Variable Fonts", "Animation"],
    readTime: "5 min read",
    cursorText: "📜 Scroll-Driven Type",
  },
];
